import { useEffect, useId, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { formatDate } from '@/format';
import { ACCEPT_ATTR, preparePicture, type PreparedPicture } from '@/loginPicture';
import { LoginBrand } from '@/screens/login/LoginBrand';
import { useCompany, DEFAULT_COMPANY, type LoginPicture } from '@/stores/company';
import { confirmAction } from '@/stores/confirm';
import { toast } from '@/stores/toast';
import { useUnsaved } from '@/stores/unsaved';

/** A picture chosen here but not saved yet. */
interface Draft {
  picture: PreparedPicture;
  /** An object URL for the preview; revoked when the draft goes. */
  url: string;
  warnings: string[];
}

const kb = (bytes: number) => `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * Settings › Company › Sign-in picture. Saved on its own, apart from the
 * company details form: it is a file, it goes to the server as soon as the MD
 * is happy with the preview, and it has nothing to undo field by field.
 *
 * The preview is the sign-in panel itself, so what is seen here is what
 * everyone gets — including the shade worked out for this picture.
 */
export function LoginPicturePanel({ editable }: { editable: boolean }) {
  const company = useCompany((s) => s.company);
  const saveLoginPicture = useCompany((s) => s.saveLoginPicture);
  const removeLoginPicture = useCompany((s) => s.removeLoginPicture);
  const saved = company.loginPicture ?? null;

  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState<'preparing' | 'saving' | 'removing' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  // An unsaved picture counts as unsaved work: leaving the page asks first.
  const formId = useId();
  const mark = useUnsaved((s) => s.mark);
  useEffect(() => {
    mark(formId, draft !== null);
  }, [draft, formId, mark]);
  useEffect(() => () => mark(formId, false), [formId, mark]);

  // Never leak the preview's object URL, whichever way the draft goes.
  useEffect(() => {
    if (!draft) return;
    return () => URL.revokeObjectURL(draft.url);
  }, [draft]);

  async function choose(file: File | undefined) {
    if (!file || busy) return;
    setError(null);
    setBusy('preparing');
    try {
      const result = await preparePicture(file);
      if (!result.ok) {
        setError(result.error);
        return;
      }
      setDraft({
        picture: result.picture,
        url: URL.createObjectURL(result.picture.blob),
        warnings: result.warnings,
      });
    } finally {
      setBusy(null);
    }
  }

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Cleared so choosing the same file again (after Cancel) still fires.
    e.target.value = '';
    void choose(file);
  }

  function onDrop(e: DragEvent) {
    if (!editable) return;
    e.preventDefault();
    setDragging(false);
    void choose(e.dataTransfer.files?.[0]);
  }

  async function save() {
    if (!draft) return;
    setError(null);
    setBusy('saving');
    try {
      await saveLoginPicture(draft.picture);
      setDraft(null);
      toast.success('Sign-in picture saved. Everyone sees it the next time the sign-in screen opens.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the picture. Try again.');
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    const ok = await confirmAction({
      title: 'Use the built-in picture?',
      message:
        'Your picture will be taken off the sign-in screen for everyone, and the built-in photograph shown again. You can choose a picture again at any time.',
      confirmLabel: 'Use built-in picture',
      cancelLabel: 'Keep my picture',
    });
    if (!ok) return;
    setError(null);
    setBusy('removing');
    try {
      await removeLoginPicture();
      toast.success('The sign-in screen shows the built-in picture again.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the picture. Try again.');
    } finally {
      setBusy(null);
    }
  }

  // The preview: the unsaved picture with its own measured shade, else the
  // saved one, else the built-in photograph.
  const previewPicture: LoginPicture | null = draft
    ? {
        url: draft.url,
        width: draft.picture.width,
        height: draft.picture.height,
        byteSize: draft.picture.blob.size,
        tint: draft.picture.tint,
        lumaTop: draft.picture.lumaTop,
        lumaMid: draft.picture.lumaMid,
        lumaBottom: draft.picture.lumaBottom,
        palette: draft.picture.palette,
        updatedAt: new Date().toISOString(),
      }
    : saved;
  const customName = company.nameEn !== DEFAULT_COMPANY.nameEn ? company.nameEn : null;

  return (
    <section className="panel loginpicture" aria-labelledby="settings-login-picture">
      <div className="formpanel__head">
        <div>
          <h2 id="settings-login-picture">Sign-in picture</h2>
          <p className="muted formgroup__hint">
            Shown beside the sign-in form on computers and tablets (not on phones). The shade over it is
            set automatically from how bright the picture is, so the writing on it stays readable.
          </p>
        </div>
      </div>

      <div className="loginpicture__body">
        <div
          className={`loginpicture__frame${dragging ? ' loginpicture__frame--drop' : ''}`}
          onDragOver={(e) => {
            if (!editable || busy) return;
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <LoginBrand
            key={previewPicture?.url ?? 'built-in'}
            picture={previewPicture}
            name={customName}
            logoDataUrl={company.logoDataUrl}
            preview
          />
          {busy === 'preparing' && (
            <div className="loginpicture__busy" role="status">
              Preparing picture…
            </div>
          )}
        </div>

        <div className="loginpicture__side">
          <p className="loginpicture__status" aria-live="polite">
            {draft ? (
              <>
                <strong>Not saved yet.</strong> This is how the sign-in screen will look.
                <span className="muted loginpicture__facts">
                  {draft.picture.width} × {draft.picture.height} · {kb(draft.picture.blob.size)}
                </span>
              </>
            ) : saved ? (
              <>
                <strong>Your picture</strong>
                <span className="muted loginpicture__facts">
                  {saved.width} × {saved.height} · {kb(saved.byteSize)} · changed {formatDate(saved.updatedAt)}
                </span>
              </>
            ) : (
              <>
                <strong>Built-in picture</strong>
                <span className="muted loginpicture__facts">No picture of your own has been chosen.</span>
              </>
            )}
          </p>

          {draft && draft.warnings.length > 0 && (
            <ul className="loginpicture__warnings">
              {draft.warnings.map((w) => (
                <li key={w}>
                  <span aria-hidden="true">⚠ </span>
                  {w}
                </li>
              ))}
            </ul>
          )}

          {error && (
            <p className="login__error" role="alert">
              <span aria-hidden="true">⚠ </span>
              {error}
            </p>
          )}

          {editable && (
            <div className="loginpicture__actions">
              <input
                ref={input}
                type="file"
                accept={ACCEPT_ATTR}
                onChange={onPick}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                data-unsaved-ignore
              />
              {draft ? (
                <>
                  <button type="button" className="btn btn--primary" onClick={() => void save()} disabled={busy !== null}>
                    {busy === 'saving' ? 'Saving…' : 'Save picture'}
                  </button>
                  <button type="button" className="btn" onClick={() => input.current?.click()} disabled={busy !== null}>
                    Choose another
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => {
                      setDraft(null);
                      setError(null);
                    }}
                    disabled={busy !== null}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="btn" onClick={() => input.current?.click()} disabled={busy !== null}>
                    {busy === 'preparing' ? 'Preparing…' : saved ? 'Change picture' : 'Choose picture'}
                  </button>
                  {saved && (
                    <button type="button" className="btn btn--ghost" onClick={() => void remove()} disabled={busy !== null}>
                      {busy === 'removing' ? 'Removing…' : 'Use built-in picture'}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {editable && !draft && (
            <p className="muted loginpicture__tips">
              A JPEG, PNG or WebP photo at least 1000 pixels on its shorter side looks best. A
              landscape or square picture fits the panel better than a tall one. You can also drop a
              picture onto the preview. It is resized and saved without any camera or location details.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
